const sections = [
  {
    id: "eligibility",
    title: "Eligibility",
    accent: "#6d4aff",
    content: (
      <p className="text-[#667085] leading-7">
        You must be at least 18 years of age or have the permission of a parent, guardian, or authorized
        organization to use DocLift. By using our services, you represent that you have the legal authority to
        accept these Terms.
      </p>
    ),
  },
  {
    id: "description",
    title: "Description of Service",
    accent: "#13a66f",
    content: (
      <p className="text-[#667085] leading-7">
        DocLift is a WhatsApp-based application that enables users to interact with our platform for document
        processing, communication, automation, and other related services. Features may change or be updated
        without prior notice.
      </p>
    ),
  },
  {
    id: "user-responsibilities",
    title: "User Responsibilities",
    accent: "#6d4aff",
    content: (
      <>
        <p className="text-[#667085] leading-7">When using DocLift, you agree to:</p>
        <ul className="mt-4 grid gap-2">
          {[
            "Provide accurate and up-to-date information.",
            "Use the service only for lawful purposes.",
            "Not upload or transmit malicious software, viruses, or harmful content.",
            "Not attempt to gain unauthorized access to our systems or other users' accounts.",
            "Not use the service in any manner that violates applicable laws or regulations.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm text-[#374151]">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#6d4aff]" />
              {item}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm text-[#667085]">
          You are responsible for all activities performed through your account or WhatsApp number.
        </p>
      </>
    ),
  },
  {
    id: "whatsapp",
    title: "WhatsApp Business Platform",
    accent: "#13a66f",
    content: (
      <p className="text-[#667085] leading-7">
        DocLift uses the official WhatsApp Business Platform provided by Meta. Your use of WhatsApp is also subject
        to Meta's applicable terms and policies. We are not responsible for the availability, functionality, or
        policies of third-party platforms.
      </p>
    ),
  },
  {
    id: "intellectual-property",
    title: "Intellectual Property",
    accent: "#6d4aff",
    content: (
      <>
        <p className="text-[#667085] leading-7">
          All content, software, trademarks, logos, designs, documentation, and technology associated with DocLift
          are the exclusive property of Orryworx Pvt. Ltd. or its licensors.
        </p>
        <p className="mt-3 text-[#667085] leading-7">
          You may not copy, modify, distribute, reverse engineer, or reproduce any part of the service without our
          prior written consent.
        </p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    title: "Acceptable Use",
    accent: "#13a66f",
    content: (
      <>
        <p className="text-[#667085] leading-7">You agree not to:</p>
        <ul className="mt-4 grid gap-2">
          {[
            "Use DocLift for fraudulent, illegal, or abusive activities.",
            "Send spam, phishing messages, or unsolicited communications.",
            "Upload content that infringes the rights of others.",
            "Attempt to disrupt or interfere with the operation or security of the platform.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-sm text-[#374151]">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#13a66f]" />
              {item}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm text-[#667085]">
          Violation of these Terms may result in suspension or termination of your access.
        </p>
      </>
    ),
  },
  {
    id: "privacy",
    title: "Privacy",
    accent: "#6d4aff",
    content: (
      <p className="text-[#667085] leading-7">
        Your use of DocLift is also governed by our{" "}
        <a href="/privacy" className="font-semibold text-[#6d4aff] hover:underline">
          Privacy Policy
        </a>
        , which explains how we collect, use, and protect your information.
      </p>
    ),
  },
  {
    id: "availability",
    title: "Service Availability",
    accent: "#13a66f",
    content: (
      <p className="text-[#667085] leading-7">
        We strive to provide reliable services but do not guarantee uninterrupted or error-free operation. We may
        suspend, modify, or discontinue any part of the service for maintenance, upgrades, or operational reasons
        without prior notice.
      </p>
    ),
  },
  {
    id: "disclaimer",
    title: "Disclaimer of Warranties",
    accent: "#6d4aff",
    content: (
      <p className="text-[#667085] leading-7">
        DocLift is provided on an "as is" and "as available" basis. To the fullest extent permitted by law,
        Orryworx Pvt. Ltd. disclaims all warranties, express or implied, including warranties of merchantability,
        fitness for a particular purpose, and non-infringement.
      </p>
    ),
  },
  {
    id: "liability",
    title: "Limitation of Liability",
    accent: "#13a66f",
    content: (
      <>
        <p className="text-[#667085] leading-7">
          To the maximum extent permitted by applicable law, Orryworx Pvt. Ltd., its directors, employees,
          partners, and affiliates shall not be liable for any indirect, incidental, special, consequential, or
          punitive damages arising from your use of or inability to use DocLift.
        </p>
        <p className="mt-3 text-[#667085] leading-7">
          Our total liability for any claim arising out of these Terms shall not exceed the amount, if any, paid by
          you for the use of the service during the preceding twelve months.
        </p>
      </>
    ),
  },
  {
    id: "termination",
    title: "Termination",
    accent: "#6d4aff",
    content: (
      <>
        <p className="text-[#667085] leading-7">
          We reserve the right to suspend or terminate your access to DocLift at any time if you violate these
          Terms, engage in unlawful activities, or misuse the service.
        </p>
        <p className="mt-3 text-[#667085] leading-7">You may stop using the service at any time.</p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to the Terms",
    accent: "#13a66f",
    content: (
      <p className="text-[#667085] leading-7">
        We may update these Terms from time to time. Any modifications will be posted on this page with a revised
        effective date. Continued use of DocLift after such changes constitutes acceptance of the updated Terms.
      </p>
    ),
  },
  {
    id: "governing-law",
    title: "Governing Law",
    accent: "#6d4aff",
    content: (
      <p className="text-[#667085] leading-7">
        These Terms shall be governed by and interpreted in accordance with the laws of India. Any disputes arising
        from these Terms shall be subject to the exclusive jurisdiction of the courts located in Gurugram, Haryana,
        India.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact Information",
    accent: "#13a66f",
    content: (
      <>
        <p className="text-[#667085] leading-7">
          If you have any questions regarding these Terms, please contact us:
        </p>
        <div className="mt-5 grid gap-2 rounded-2xl border border-[rgba(19,166,111,0.14)] bg-white/70 p-5 text-sm backdrop-blur-sm">
          {[
            ["Company", "Orryworx Pvt. Ltd."],
            ["Application", "DocLift"],
          ].map(([label, value]) => (
            <div key={label} className="flex gap-2">
              <span className="w-24 font-semibold text-[#374151]">{label}</span>
              <span className="text-[#667085]">{value}</span>
            </div>
          ))}
          <div className="flex gap-2">
            <span className="w-24 font-semibold text-[#374151]">Email</span>
            <a href="mailto:info@orryworx.com" className="text-[#13a66f] hover:underline">
              info@orryworx.com
            </a>
          </div>
          <div className="flex gap-2">
            <span className="w-24 font-semibold text-[#374151]">Website</span>
            <a href="http://www.orryworx.com" target="_blank" rel="noreferrer" className="text-[#13a66f] hover:underline">
              www.orryworx.com
            </a>
          </div>
        </div>
      </>
    ),
  },
];

const TermsOfService = () => {
  return (
    <div
      className="min-h-screen"
      style={{
        background:
          "radial-gradient(circle at 14% 18%, rgba(19,166,111,0.08), transparent 26%), radial-gradient(circle at 86% 78%, rgba(109,74,255,0.07), transparent 28%), linear-gradient(180deg,#ffffff 0%,#f8fff9 52%,#fbfaff 100%)",
      }}
    >
      {/* Hero banner */}
      <div className="border-b border-[rgba(19,166,111,0.12)] bg-white/60 backdrop-blur-sm">
        <div className="mx-auto max-w-[1100px] px-5 py-14 sm:px-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-[rgba(19,166,111,0.2)] bg-[rgba(19,166,111,0.07)] px-4 py-1.5 text-xs font-bold text-[#13a66f]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#13a66f]" />
            Legal
          </span>
          <h1 className="mt-5 text-4xl font-black tracking-tight text-[#101522] sm:text-5xl">
            Terms of Service
          </h1>
          <p className="mt-3 text-sm text-[#667085]">
            Effective Date: <span className="font-semibold text-[#374151]">July 10, 2026</span>
          </p>
          <p className="mt-5 max-w-2xl text-base leading-7 text-[#667085]">
            Welcome to <span className="font-semibold text-[#374151]">DocLift</span>, a service provided by{" "}
            <span className="font-semibold text-[#374151]">Orryworx Pvt. Ltd.</span> These Terms of Service govern
            your access to and use of the DocLift application, website, and related services. By accessing or using
            DocLift, you agree to be bound by these Terms.
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto max-w-[1100px] px-5 py-12 sm:px-8 lg:grid lg:grid-cols-[220px_1fr] lg:gap-12">
        {/* Sticky TOC */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl border border-[rgba(19,166,111,0.12)] bg-white/70 p-5 backdrop-blur-sm">
            <p className="mb-3 text-xs font-bold uppercase tracking-widest text-[#13a66f]">Contents</p>
            <nav className="grid gap-1">
              {sections.map((s, i) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-semibold text-[#667085] transition hover:bg-[rgba(19,166,111,0.06)] hover:text-[#13a66f]"
                >
                  <span className="text-[10px] font-bold text-[#13a66f] opacity-60">{String(i + 1).padStart(2, "0")}</span>
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Sections */}
        <main className="grid gap-6">
          {sections.map((s, i) => (
            <section
              key={s.id}
              id={s.id}
              className="rounded-2xl border border-[rgba(19,166,111,0.1)] bg-white/70 p-6 backdrop-blur-sm transition hover:border-[rgba(19,166,111,0.22)] hover:shadow-[0_18px_52px_rgba(19,166,111,0.07)] sm:p-8"
            >
              <div className="mb-4 flex items-center gap-3">
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-xs font-black text-white"
                  style={{ background: s.accent }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="text-lg font-black text-[#101522]">{s.title}</h2>
              </div>
              {s.content}
            </section>
          ))}
        </main>
      </div>
    </div>
  );
};

export default TermsOfService;
